/**
 * KOSIMA: size guide drawer (snippets/size-guide.liquid).
 *
 * Two small jobs, both progressive enhancement -- the tables are complete server-rendered HTML in
 * centimetres, with the size that was selected on page load already marked:
 *
 *   1. Keep the highlighted size column in step with the variant picker. Prestige dispatches
 *      "variant:change" on the product form (bubbles: true) with the selected variant, so this
 *      listens at document level and compares the variant's value for the size option against
 *      each cell's data-size. Document level on purpose: <x-drawer> moves itself to <body> when it
 *      opens, so a listener bound to an ancestor in the info column would be left behind.
 *
 *   2. The Centimetres / Inches toggle. Every measurement cell carries its authored centimetre
 *      text in data-cm; inches are computed from that, never from the displayed text, so toggling
 *      back and forth cannot drift. Ranges like "96-100" convert each number. The choice is kept
 *      in localStorage so it holds across products.
 */
const UNIT_STORAGE_KEY = 'kosima:size-guide-unit';
const NUMBER_PATTERN = /\d+(?:[.,]\d+)?/g;

function toInches(text) {
  return text.replace(NUMBER_PATTERN, (match) => {
    const cm = parseFloat(match.replace(',', '.'));
    if (Number.isNaN(cm)) return match;
    const inches = Math.round((cm / 2.54) * 10) / 10;
    return String(inches);
  });
}

class SizeGuide extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });
    this.addEventListener('click', this.onClick.bind(this), { signal });

    let unit = 'cm';
    try {
      unit = localStorage.getItem(UNIT_STORAGE_KEY) || 'cm';
    } catch (error) {}
    this.setUnit(unit, false);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onVariantChange(event) {
    if (event.detail?.formId !== this.getAttribute('form-id')) return;

    const variant = event.detail.variant;
    const index = Number(this.getAttribute('option-position')) - 1;
    const size = variant?.options?.[index] ?? variant?.[`option${index + 1}`];
    if (size == null) return;

    this.querySelectorAll('[data-size]').forEach((cell) => {
      cell.classList.toggle('is-selected', cell.dataset.size === String(size));
    });
  }

  onClick(event) {
    const button = event.target.closest('[data-unit]');
    if (!button || !this.contains(button)) return;
    this.setUnit(button.dataset.unit, true);
  }

  setUnit(unit, persist) {
    if (unit !== 'in') unit = 'cm';

    this.querySelectorAll('[data-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.unit === unit));
    });

    this.querySelectorAll('[data-cm]').forEach((cell) => {
      const cm = cell.dataset.cm.trim();
      cell.textContent = unit === 'in' ? toInches(cm) : cm;
    });

    if (persist) {
      try {
        localStorage.setItem(UNIT_STORAGE_KEY, unit);
      } catch (error) {}
    }
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
